import { useState, useRef } from "react";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import About from "../components/About";
import emailjs from "@emailjs/browser";
import {
  Code,
  Smartphone,
  Monitor,
  Mail,
  Send,
  ArrowRight,
  Sparkles,
  Zap,
  Shield,
  CheckCircle,
  AlertCircle,
} from "lucide-react";

export default function RaluiTech() {
  const formRef = useRef(); // Referência para o formulário
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState(null); // 'success' ou 'error'

  const [formData, setFormData] = useState({
    name: "",
    email: "",
    message: "",
  });

  const services = [
    {
      icon: <Code className="w-8 h-8" />,
      title: "Desenvolvimento Web",
      description:
        "Sites modernos, responsivos e otimizados, utilizando as melhores tecnologias do mercado.",
      gradient: "from-blue-500 to-purple-600",
    },
    {
      icon: <Monitor className="w-8 h-8" />,
      title: "Sistemas Desktop",
      description:
        "Aplicações desktop robustas e personalizadas para automatizar e otimizar seus processos.",
      gradient: "from-purple-500 to-pink-600",
    },
    {
      icon: <Smartphone className="w-8 h-8" />,
      title: "Soluções Personalizadas",
      description:
        "Desenvolvimento sob medida para sua empresa. Do conceito à entrega, transformamos ideias em realidade.",
      gradient: "from-green-500 to-teal-600",
    },
  ];

  const handleSubmit = (e) => {
    e.preventDefault();
    setLoading(true);
    setStatus(null);

    // --- CONFIGURAÇÃO DO EMAILJS ---
    emailjs
      .sendForm(
        "service_uz4qpq7", // Ex: service_uz4qpq7
        "template_yvp1h7t", // Ex: template_x9d8s7
        formRef.current,
        "H3Bve0CLClJ5ugiHF", // Ex: user_98s7df987s (Fica em Account > API Keys)
      )
      .then(
        (result) => {
          setLoading(false);
          setStatus("success");
          setFormData({ name: "", email: "", message: "" }); // Limpa o formulário
        },
        (error) => {
          setLoading(false);
          setStatus("error");
          console.error("Erro no envio:", error.text);
        },
      );
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-purple-950 to-slate-950 text-white overflow-x-hidden">
      {/* Background Blobs */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-20 -left-20 w-96 h-96 bg-purple-600 rounded-full mix-blend-multiply filter blur-3xl opacity-20 animate-blob"></div>
        <div className="absolute top-40 -right-20 w-96 h-96 bg-blue-600 rounded-full mix-blend-multiply filter blur-3xl opacity-20 animate-blob animation-delay-2000"></div>
        <div className="absolute -bottom-20 left-1/2 w-96 h-96 bg-green-600 rounded-full mix-blend-multiply filter blur-3xl opacity-20 animate-blob animation-delay-4000"></div>
      </div>

      <Navbar />

      {/* Hero Section */}
      <section
        id="home"
        className="relative z-10 min-h-screen flex items-center justify-center px-6 pt-32 pb-20"
      >
        <div className="max-w-6xl mx-auto text-center space-y-8">
          <div className="animate-fade-in-up">
            <div className="inline-block p-4 rounded-3xl bg-gradient-to-br from-white/10 to-white/5 backdrop-blur-sm border border-white/20 shadow-2xl hover:scale-105 transition-transform duration-500">
              <div className="w-64 h-64 md:w-96 md:h-96 mx-auto relative">
                <div className="absolute inset-0 bg-gradient-to-br from-purple-500 via-blue-500 to-green-500 rounded-2xl opacity-20 blur-xl animate-pulse"></div>
                <img
                  src="/assets/ralui-tech-logo.png" // Confirme se o nome do arquivo está certo
                  alt="RALUI TECH Logo"
                  className="w-full h-full object-contain relative z-10 drop-shadow-2xl"
                  onError={(e) => {
                    e.target.style.display = "none";
                  }}
                />
              </div>
            </div>
          </div>

          <div className="space-y-6 animate-fade-in-up animation-delay-200">
            <h1 className="text-5xl md:text-7xl font-bold leading-tight">
              <span className="bg-gradient-to-r from-purple-400 via-blue-400 to-green-400 bg-clip-text text-transparent">
                Transformando
              </span>
              <br />
              <span className="text-white">Ideias em Código</span>
            </h1>
            <p className="text-xl md:text-2xl text-slate-300 max-w-3xl mx-auto">
              Desenvolvimento de software profissional para empresas que buscam
              inovação e resultados reais.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center pt-8">
              <a
                href="#contact"
                className="group px-8 py-4 bg-gradient-to-r from-purple-600 to-blue-600 rounded-full font-semibold text-lg hover:shadow-2xl hover:shadow-purple-500/50 transition-all flex items-center gap-2"
              >
                Iniciar Projeto
                <ArrowRight
                  className="group-hover:translate-x-1 transition-transform"
                  size={20}
                />
              </a>
              <a
                href="#services"
                className="px-8 py-4 border-2 border-purple-500/50 rounded-full font-semibold text-lg hover:bg-purple-500/10 transition-all"
              >
                Conhecer Serviços
              </a>
            </div>
          </div>

          {/* Stats */}
          <div className="grid grid-cols-2 md:grid-cols-3 gap-8 pt-16 max-w-4xl mx-auto animate-fade-in-up animation-delay-400">
            <div className="p-6 rounded-xl bg-white/5 backdrop-blur-sm border border-white/10 hover:border-purple-500/50 transition-all">
              <div className="flex items-center justify-center gap-2 mb-2">
                <Zap className="text-yellow-400" size={24} />
                <div className="text-3xl font-bold">100%</div>
              </div>
              <div className="text-slate-400">Dedicação</div>
            </div>
            <div className="p-6 rounded-xl bg-white/5 backdrop-blur-sm border border-white/10 hover:border-blue-500/50 transition-all">
              <div className="flex items-center justify-center gap-2 mb-2">
                <Shield className="text-blue-400" size={24} />
                <div className="text-3xl font-bold">Código</div>
              </div>
              <div className="text-slate-400">Limpo & Seguro</div>
            </div>
            <div className="p-6 rounded-xl bg-white/5 backdrop-blur-sm border border-white/10 hover:border-green-500/50 transition-all col-span-2 md:col-span-1">
              <div className="flex items-center justify-center gap-2 mb-2">
                <Sparkles className="text-green-400" size={24} />
                <div className="text-3xl font-bold">Moderno</div>
              </div>
              <div className="text-slate-400">Tecnologias Atuais</div>
            </div>
          </div>
        </div>
      </section>

      {/* Sobre Nós */}
      <About />

      {/* Services Section */}
      <section
        id="services"
        className="relative z-10 px-6 py-32 bg-gradient-to-b from-transparent via-purple-950/30 to-transparent"
      >
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16 space-y-4">
            <h2 className="text-4xl md:text-5xl font-bold">
              <span className="bg-gradient-to-r from-purple-400 to-blue-400 bg-clip-text text-transparent">
                Nossos Serviços
              </span>
            </h2>
            <p className="text-xl text-slate-300 max-w-2xl mx-auto">
              Soluções completas em tecnologia para impulsionar seu negócio
            </p>
          </div>
          <div className="grid md:grid-cols-3 gap-8">
            {services.map((service, index) => (
              <div
                key={index}
                className="group p-8 rounded-2xl bg-white/5 backdrop-blur-sm border border-white/10 hover:border-white/30 transition-all duration-300 hover:scale-105 hover:shadow-2xl"
              >
                <div
                  className={`w-16 h-16 rounded-xl bg-gradient-to-br ${service.gradient} flex items-center justify-center mb-6 group-hover:scale-110 transition-transform`}
                >
                  {service.icon}
                </div>
                <h3 className="text-2xl font-bold mb-4">{service.title}</h3>
                <p className="text-slate-300 leading-relaxed">
                  {service.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="relative z-10 px-6 py-32">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-16 space-y-4">
            <h2 className="text-4xl md:text-5xl font-bold">
              <span className="bg-gradient-to-r from-purple-400 to-blue-400 bg-clip-text text-transparent">
                Vamos Conversar?
              </span>
            </h2>
            <p className="text-xl text-slate-300">
              Conte-nos sobre seu projeto. Estamos prontos para transformar sua
              visão em realidade.
            </p>
          </div>

          <form
            ref={formRef}
            onSubmit={handleSubmit}
            className="space-y-6 p-8 rounded-2xl bg-white/5 backdrop-blur-sm border border-white/10"
          >
            <div>
              <label className="block text-sm font-medium mb-2 text-slate-300">
                Nome
              </label>
              <input
                type="text"
                name="name" // Essencial para o EmailJS
                required
                value={formData.name}
                onChange={(e) =>
                  setFormData({ ...formData, name: e.target.value })
                }
                className="w-full px-4 py-3 rounded-lg bg-white/5 border border-white/10 focus:border-purple-500 focus:outline-none focus:ring-2 focus:ring-purple-500/50 transition-all text-white"
                placeholder="Seu nome"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-2 text-slate-300">
                Email
              </label>
              <input
                type="email"
                name="email" // Essencial para o EmailJS
                required
                value={formData.email}
                onChange={(e) =>
                  setFormData({ ...formData, email: e.target.value })
                }
                className="w-full px-4 py-3 rounded-lg bg-white/5 border border-white/10 focus:border-purple-500 focus:outline-none focus:ring-2 focus:ring-purple-500/50 transition-all text-white"
                placeholder="seu@email.com"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-2 text-slate-300">
                Mensagem
              </label>
              <textarea
                name="message" // Essencial para o EmailJS
                required
                rows={5}
                value={formData.message}
                onChange={(e) =>
                  setFormData({ ...formData, message: e.target.value })
                }
                className="w-full px-4 py-3 rounded-lg bg-white/5 border border-white/10 focus:border-purple-500 focus:outline-none focus:ring-2 focus:ring-purple-500/50 transition-all resize-none text-white"
                placeholder="Conte-nos sobre seu projeto..."
              />
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full py-4 bg-gradient-to-r from-purple-600 to-blue-600 rounded-lg font-semibold text-lg hover:shadow-2xl hover:shadow-purple-500/50 transition-all flex items-center justify-center gap-2 group disabled:opacity-70 disabled:cursor-not-allowed"
            >
              {loading ? "Enviando..." : "Enviar Mensagem"}
              {!loading && (
                <Send
                  className="group-hover:translate-x-1 transition-transform"
                  size={20}
                />
              )}
            </button>

            {/* Feedback de Sucesso */}
            {status === "success" && (
              <div className="flex items-center gap-2 text-green-400 bg-green-400/10 p-4 rounded-lg animate-fade-in border border-green-400/20">
                <CheckCircle size={20} />
                <span>
                  Mensagem enviada com sucesso! Entraremos em contato.
                </span>
              </div>
            )}

            {/* Feedback de Erro */}
            {status === "error" && (
              <div className="flex items-center gap-2 text-red-400 bg-red-400/10 p-4 rounded-lg animate-fade-in border border-red-400/20">
                <AlertCircle size={20} />
                <span>
                  Erro ao enviar. Por favor, tente pelo WhatsApp abaixo.
                </span>
              </div>
            )}
          </form>

          {/* Botão Extra WhatsApp */}
          <div className="mt-12 flex flex-col sm:flex-row gap-6 justify-center items-center text-center">
            <a
              href="https://wa.me/5571982946930" // SEU ZAP AQUI
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-2 text-green-400 hover:text-green-300 font-bold text-lg transition-colors border border-green-500/30 px-6 py-3 rounded-full hover:bg-green-500/10"
            >
              <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
                <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z" />
              </svg>
              (71) 98294-6930
            </a>

            <a
              href="mailto:contato@raluitech.com"
              className="flex items-center gap-2 text-slate-300 hover:text-purple-400 transition-colors"
            >
              <Mail size={20} /> contato@raluitech.com
            </a>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
